import React, { useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";

// Options de plan COHÉRENTES avec le persona (rôle) du compte.
// Changer de persona (ex. collectionneur → family office) = un changement de RÔLE,
// pas un simple changement de plan → cela se fait via « Rôles & permissions ».
function planOptions(role, foundationMode) {
  if (role === "expert") {
    return [
      ["art_advisor", "Art Advisor"],
      ["art_advisor_premium", "Art Advisor Premium"],
    ];
  }
  if (role === "family_office") {
    return [["family_office", "Family Office"]];
  }
  if (foundationMode) {
    return [
      ["standard", "Standard"],
      ["premium", "Premium"],
      ["fondation", "Fondation"],
    ];
  }
  if (role === "user") {
    return [
      ["standard", "Standard"],
      ["premium", "Premium"],
    ];
  }
  // team / admin / partner / notaire / autres : le plan n'est pas applicable ici.
  return [];
}

// Statut affiché / modifiable. « Démo » = is_demo (accès sans paiement, ex. comptes de test et de démonstration) ;
// les autres valeurs correspondent à stripe_subscription_status (réécrit par Stripe au prochain événement d'abonnement).
const STATUS_OPTIONS = [
  ["demo", "Démo"],
  ["active", "Actif"],
  ["unpaid", "Impayé"],
  ["canceled", "Annulé"],
  ["none", "Non défini"],
];
const statusOf = (u) => (u.is_demo ? "demo" : (u.stripe_subscription_status || "none"));

export default function AdminChangeSubscription() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    async function loadUsers() {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, plan, role, foundation_mode, gallery_mode, is_demo, stripe_subscription_status")
        .order("last_name", { ascending: true });
      if (error) console.error(error);
      else setUsers(data || []);
      setLoading(false);
    }
    loadUsers();
  }, []);

  async function handleChangePlan(id, newPlan) {
    setSavingId(id);
    const { error } = await supabase
      .from("profiles")
      .update({ plan: newPlan, updated_at: new Date().toISOString() })
      .eq("id", id);
    setSavingId(null);
    if (error) {
      console.error(error);
      setMessage("❌ Erreur lors de la mise à jour.");
    } else {
      setUsers(users.map((u) => (u.id === id ? { ...u, plan: newPlan } : u)));
      setMessage("✅ Abonnement mis à jour avec succès !");
      setTimeout(() => setMessage(""), 2500);
    }
  }

  async function handleChangeStatus(id, value) {
    const patch = value === "demo"
      ? { is_demo: true }
      : { is_demo: false, stripe_subscription_status: value === "none" ? null : value };
    setSavingId(id);
    const { error } = await supabase
      .from("profiles")
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq("id", id);
    setSavingId(null);
    if (error) {
      console.error(error);
      setMessage("❌ Erreur lors de la mise à jour du statut.");
    } else {
      setUsers(users.map((u) => (u.id === id ? { ...u, ...patch } : u)));
      setMessage("✅ Statut mis à jour.");
      setTimeout(() => setMessage(""), 2500);
    }
  }

  // Compte galerie : active, pour ce compte seulement, les champs et pages propres aux galeries.
  async function handleToggleGallery(id, enabled) {
    setSavingId(id);
    const { error } = await supabase
      .from("profiles")
      .update({ gallery_mode: enabled, updated_at: new Date().toISOString() })
      .eq("id", id);
    setSavingId(null);
    if (error) {
      console.error(error);
      setMessage("❌ Erreur lors de la mise à jour du compte galerie.");
    } else {
      setUsers(users.map((u) => (u.id === id ? { ...u, gallery_mode: enabled } : u)));
      setMessage(enabled ? "✅ Compte galerie activé." : "✅ Compte galerie désactivé.");
      setTimeout(() => setMessage(""), 2500);
    }
  }

  if (loading) return <div>Chargement des profils...</div>;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-900">💳 Modifier les abonnements</h2>
      <p className="text-sm text-gray-500">
        Les plans proposés correspondent au <strong>persona</strong> du compte (rôle). Pour changer de persona
        (ex. collectionneur → Family Office), passez par « Rôles &amp; permissions ».
      </p>
      <p className="text-sm text-gray-500">
        <strong>Statut</strong> : « Démo » donne l'accès sans paiement (comptes de test et de démonstration). Les autres valeurs
        sont celles de l'abonnement Stripe, que Stripe peut réécrire au prochain événement. Pour un essai à durée limitée,
        utilisez plutôt « Accorder un accès gratuit » (date de fin). <strong>Compte galerie</strong> : affiche, pour ce
        compte seulement, la nature de l'ensemble, le contexte de transmission, le statut patrimonial des œuvres et la page
        « Continuité et transmission ».
      </p>
      {message && <div className="text-sm text-green-600">{message}</div>}
      <table className="min-w-full border border-gray-200 text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-3 py-2 text-left">Nom</th>
            <th className="px-3 py-2 text-left">Rôle</th>
            <th className="px-3 py-2 text-left">Abonnement actuel</th>
            <th className="px-3 py-2 text-left">Changer</th>
            <th className="px-3 py-2 text-left">Statut</th>
            <th className="px-3 py-2 text-left">Compte galerie</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const opts = planOptions(u.role, u.foundation_mode);
            // Affiche le plan courant même s'il est hors persona (legacy), sans l'offrir aux autres.
            const hasCurrent = u.plan && opts.some(([v]) => v === u.plan);
            const displayOpts = hasCurrent || !u.plan ? opts : [[u.plan, `${u.plan} (actuel)`], ...opts];
            const roleLabel = u.foundation_mode ? "fondation" : u.role;
            return (
              <tr key={u.id} className="border-t">
                <td className="px-3 py-2">{u.first_name} {u.last_name}</td>
                <td className="px-3 py-2">{roleLabel}</td>
                <td className="px-3 py-2 font-medium">{u.plan || "—"}</td>
                <td className="px-3 py-2">
                  {opts.length === 0 ? (
                    <span className="text-gray-400">Non applicable</span>
                  ) : (
                    <select
                      value={u.plan || ""}
                      disabled={savingId === u.id}
                      onChange={(e) => handleChangePlan(u.id, e.target.value)}
                      className="border rounded-md px-2 py-1 disabled:opacity-50"
                    >
                      {displayOpts.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  )}
                </td>
                <td className="px-3 py-2">
                  <select
                    value={statusOf(u)}
                    disabled={savingId === u.id}
                    onChange={(e) => handleChangeStatus(u.id, e.target.value)}
                    className="border rounded-md px-2 py-1 disabled:opacity-50"
                  >
                    {STATUS_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2">
                  {u.role === "user" && !u.foundation_mode ? (
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={u.gallery_mode === true}
                        disabled={savingId === u.id}
                        onChange={(e) => handleToggleGallery(u.id, e.target.checked)}
                      />
                      <span className="text-xs text-gray-600">{u.gallery_mode ? "Galerie" : "—"}</span>
                    </label>
                  ) : (
                    <span className="text-gray-400">Non applicable</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
